// src/components/GearTable.jsx
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGearStore, defaultFields } from "../store/gearStore";
import { useTeamStore } from "../store/teamStore";
import { useProjectStore } from "../store/projectStore";
import {
  MoreVertical,
  X,
  Filter,
  Pencil,
  Trash2,
  Settings,
  Download,
  Upload,
  FileText,
  Repeat,
} from "lucide-react";
import { api } from "../lib/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import TransferEquipmentModal from "./TransferEquipmentModal";
import GearModal from "./GearModal";
import { useUIStore } from "../store/uiStore";

// ----------- Modal close on click-outside + ESC handler -----------
function useOnClickOutsideAndEsc(ref, handler, active = true) {
  useEffect(() => {
    if (!active) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) handler();
    }
    function handleEsc(e) {
      if (e.key === "Escape") handler();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [ref, handler, active]);
}

// ----------- Helper: Auto-generate field names in camelCase -----------
function toCamelCase(str) {
  return str
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/\s(.)/g, function (match, group1) {
      return group1.toUpperCase();
    })
    .replace(/\s/g, "")
    .replace(/^(.)/, function (match, group1) {
      return group1.toLowerCase();
    });
}

// Utility: Detect outside clicks for menus/popups
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    function listener(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    }
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
}

// ----------- CSV EXPORT (with names instead of IDs) -----------
function exportGearAsCSV(fields, data, { team, projects }) {
  const csv = Papa.unparse(
    data.map((row) => {
      const mapped = {};
      fields.forEach((f) => {
        let val = row[f.name] ?? "";
        if (f.name === "assignedTo" && val) {
          const user = team.find((u) => u.id === val);
          val = user ? user.name || user.email || val : "";
        }
        if (f.name === "assignedProject" && val) {
          const project = projects.find((p) => p.id === val);
          val = project ? project.name || val : "";
        }
        mapped[f.label] = val;
      });
      return mapped;
    })
  );
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "gear-export.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ----------- PDF EXPORT (with names instead of IDs) -----------
function exportGearAsPDF(fields, data, { team, projects }) {
  const docu = new jsPDF();
  docu.text("Gear Inventory", 14, 18);
  autoTable(docu, {
    head: [fields.map((f) => f.label)],
    body: data.map((row) =>
      fields.map((f) => {
        let val = row[f.name] ?? "";
        if (f.name === "assignedTo" && val) {
          const user = team.find((u) => u.id === val);
          val = user ? user.name || user.email || val : "";
        }
        if (f.name === "assignedProject" && val) {
          const project = projects.find((p) => p.id === val);
          val = project ? project.name || val : "";
        }
        return val;
      })
    ),
    startY: 24,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] },
    margin: { left: 14, right: 14 },
  });
  docu.save("gear-export.pdf");
}

// ----------- CSV IMPORT -----------
function importCSVtoGear({ fields, setShowImportError, onImport }) {
  return (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        if (!Array.isArray(results.data)) {
          setShowImportError("Failed to read CSV rows.");
          return;
        }
        const csvRows = results.data;
        const mappedRows = csvRows
          .filter((row) => Object.values(row).some((val) => val && val.trim() !== ""))
          .map((csvRow) => {
            const newRow = {};
            fields.forEach((f) => {
              newRow[f.name] = csvRow[f.label] || "";
            });
            return newRow;
          });
        if (mappedRows.length === 0) {
          setShowImportError("No valid rows found in CSV.");
        } else {
          setShowImportError("");
          onImport(mappedRows);
        }
      },
      error: (err) => {
        setShowImportError("Failed to parse CSV: " + err.message);
      },
    });
    e.target.value = "";
  };
}

export function GearTable({
  setSelectedGear,
  setCurrentPage,
  archiveMode = false,
  onActiveCountDelta,
  onRefreshStats,
  statsRow,
}) {
  const navigate = useNavigate();
  const {
    gear,
    categories,
    statuses,
    setCategories,
    setGear,
    fields,
    setFields,
  } = useGearStore();
  const { team, setTeam } = useTeamStore();
  const { projects } = useProjectStore();

  // ✅ normalize all lists to arrays to avoid .filter/.map crashes
  const safeGear = Array.isArray(gear) ? gear : [];
  const safeTeam = Array.isArray(team) ? team : [];
  const safeProjects = Array.isArray(projects) ? projects : [];
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeStatuses = Array.isArray(statuses) ? statuses : [];

  const [currentUserRole, setCurrentUserRole] = useState("user");
  useEffect(() => {
    const me = JSON.parse(localStorage.getItem('user') || 'null');
    setCurrentUserRole(me?.role || "user");
  }, []);

  // Keep team list safe (until team fetching is wired here)
  useEffect(() => {
    setTeam((prev) => (Array.isArray(prev) ? prev : []));
  }, [setTeam]);
  const isAdminOrManager = ["admin", "manager"].includes(currentUserRole);
  const isAdmin = currentUserRole === "admin";
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ category: "All", status: "All" });
  const [filterOpen, setFilterOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalGear, setModalGear] = useState(null);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [showCatModal, setShowCatModal] = useState(false);
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [showImportError, setShowImportError] = useState("");
  const [showImportSuccess, setShowImportSuccess] = useState("");

  const [toast, setToast] = useState(null); // { message, actionLabel?, onAction? }
  const toastTimerRef = useRef(null);

  const showToast = (next) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(next);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };

  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgePassword, setPurgePassword] = useState("");
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState("");

  const showTransferModal = useUIStore((s) => s.showTransferModal);
  const setShowTransferModal = useUIStore((s) => s.setShowTransferModal);

  const [menuPosition, setMenuPosition] = useState({});
  const btnRefs = useRef({});
  const filterBtnRef = useRef();
  const filterMenuRef = useRef();

  const [gearMenuOpen, setGearMenuOpen] = useState(false);
  const gearMenuRef = useRef();
  useOnClickOutside(gearMenuRef, () => setGearMenuOpen(false));
  useOnClickOutside(filterMenuRef, () => setFilterOpen(false));

  // Add ESC for filter/gear menu
  useEffect(() => {
    const escHandler = (e) => {
      if (e.key === "Escape") {
        setFilterOpen(false);
        setGearMenuOpen(false);
      }
    };
    window.addEventListener("keydown", escHandler);
    return () => window.removeEventListener("keydown", escHandler);
  }, []);

  // Modal refs and click-outside/ESC handlers
  const catModalRef = useRef();
  const fieldsModalRef = useRef();
  const transferModalRef = useRef();
  const purgeModalRef = useRef();

  useOnClickOutsideAndEsc(catModalRef, () => setShowCatModal(false), showCatModal);
  useOnClickOutsideAndEsc(fieldsModalRef, () => setShowFieldsModal(false), showFieldsModal);
  useOnClickOutsideAndEsc(transferModalRef, () => setShowTransferModal(false), showTransferModal);
  useOnClickOutsideAndEsc(purgeModalRef, () => setShowPurgeModal(false), showPurgeModal);

  const modalRef = useRef();
  const escCloseModal = useCallback(
    (e) => {
      if (e.key === "Escape") setShowModal(false);
    },
    []
  );
  useEffect(() => {
    if (showModal) {
      document.addEventListener("keydown", escCloseModal);
      return () => document.removeEventListener("keydown", escCloseModal);
    }
  }, [showModal, escCloseModal]);
  useOnClickOutside(modalRef, () => setShowModal(false));

  const menuRef = useRef();
  const activeMenuBtnRef = useRef(null);

  const computeMenuPosition = useCallback((btnEl, menuEl) => {
    if (!btnEl) return;

    const btnRect = btnEl.getBoundingClientRect();

    const menuRect = menuEl ? menuEl.getBoundingClientRect() : null;
    const menuWidth = menuRect?.width || 170;
    const menuHeight = menuRect?.height || 180;

    const pad = 12; // viewport padding
    const gap = 8;

    // Horizontal: center on button, clamp to viewport
    let left = btnRect.left + btnRect.width / 2 - menuWidth / 2;
    if (left + menuWidth > window.innerWidth - pad) {
      left = window.innerWidth - menuWidth - pad;
    }
    if (left < pad) left = pad;

    // Vertical: prefer below, but flip above if it would overflow bottom
    let top = btnRect.bottom + gap;
    if (top + menuHeight > window.innerHeight - pad) {
      top = btnRect.top - menuHeight - gap;
    }

    // Final clamp
    if (top < pad) top = pad;
    if (top + menuHeight > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - menuHeight - pad);
    }

    setMenuPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpenFor) return;
    if (!activeMenuBtnRef.current) return;

    // Recompute AFTER the menu is actually rendered so we can measure real height/width
    computeMenuPosition(activeMenuBtnRef.current, menuRef.current);
  }, [menuOpenFor, computeMenuPosition]);

  useEffect(() => {
    if (!menuOpenFor) return;

    const reposition = () => {
      if (!activeMenuBtnRef.current) return;
      computeMenuPosition(activeMenuBtnRef.current, menuRef.current);
    };

    // Capture scroll events from ANY scroll container
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [menuOpenFor, computeMenuPosition]);

  useEffect(() => {
    if (!menuOpenFor) return;

    const handlePointerDown = (e) => {
      const menuEl = menuRef.current;
      const btnEl = activeMenuBtnRef.current;

      // Click inside menu -> ignore
      if (menuEl && menuEl.contains(e.target)) return;

      // Click on the active 3-dot button -> ignore (let handleMenuOpen toggle)
      if (btnEl && btnEl.contains(e.target)) return;

      // Click anywhere else -> close
      setMenuOpenFor(null);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpenFor(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpenFor]);

  const filtered = safeGear.filter((g) => {
    const archived = !!g.isArchived;
    if (archiveMode ? !archived : archived) return false;

    const matchesSearch =
      g.name?.toLowerCase().includes(search.toLowerCase()) ||
      (g.serialNumber || "").toLowerCase().includes(search.toLowerCase());
    const matchesCategory = filter.category === "All" || g.category === filter.category;
    const matchesStatus = filter.status === "All" || g.status === filter.status;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // --- Persist to backend (POST/PATCH) + update store ---
  const handleSaveGear = async (data, id = null) => {
    setShowImportError("");

    try {
      // api may return axios-like { data } OR the JSON object directly
      const unwrap = (res) => {
        const payload = res && typeof res === "object" && "data" in res ? res.data : res;
        return payload && typeof payload === "object" ? payload : null;
      };

      if (id) {
        // Backend uses PATCH for updates
        const res = await api.patch(`/gear/${id}`, data);
        const updated = unwrap(res);

        setGear((prev) =>
          (Array.isArray(prev) ? prev : []).map((g) =>
            g.id === id ? { ...g, ...(updated || data) } : g
          )
        );
      } else {
        const res = await api.post("/gear", data);
        const created = unwrap(res);

        // If backend didn't return an object with an id, treat as failure
        if (!created?.id) {
          throw new Error("Backend did not return created gear with an id.");
        }

        setGear((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);
      }

      setShowModal(false);
    } catch (e) {
      console.warn("Failed to save gear to backend", e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to save gear.";

      setShowImportError(msg);

      // IMPORTANT: rethrow so GearModal shows the error instead of “nothing happens”
      throw new Error(msg);
    }
  };

  const handleArchiveToggle = async (g) => {
    if (!isAdmin) {
      showToast({ message: "Only admins can archive/unarchive assets." });
      return;
    }

    const prevVal = !!g.isArchived;
    const nextVal = !prevVal;

    // Total assets tracks ACTIVE (not archived)
    const delta = nextVal ? -1 : 1;
    if (typeof onActiveCountDelta === "function") onActiveCountDelta(delta);

    // optimistic update
    setGear((prev) =>
      (Array.isArray(prev) ? prev : []).map((x) =>
        x.id === g.id ? { ...x, isArchived: nextVal } : x
      )
    );

    showToast({
      message: nextVal ? "Archived equipment" : "Restored equipment",
      actionLabel: "Undo",
      onAction: async () => {
        // revert stats + locally
        if (typeof onActiveCountDelta === "function") onActiveCountDelta(-delta);

        // revert locally
        setGear((prev) =>
          (Array.isArray(prev) ? prev : []).map((x) =>
            x.id === g.id ? { ...x, isArchived: prevVal } : x
          )
        );

        try {
          await api.patch(`/gear/${g.id}`, { isArchived: prevVal });
          if (typeof onRefreshStats === "function") await onRefreshStats();
        } catch (err) {
          console.warn("Failed to undo archive toggle", err);
        }
      },
    });

    try {
      await api.patch(`/gear/${g.id}`, { isArchived: nextVal });
      if (typeof onRefreshStats === "function") await onRefreshStats();
    } catch (err) {
      console.warn("Failed to toggle archive", err);

      // rollback stats + state if server fails
      if (typeof onActiveCountDelta === "function") onActiveCountDelta(-delta);

      setGear((prev) =>
        (Array.isArray(prev) ? prev : []).map((x) =>
          x.id === g.id ? { ...x, isArchived: prevVal } : x
        )
      );

      showToast({ message: "Failed to update archive status" });
    }
  };

  const handleDeleteGear = async (id) => {
    if (!isAdmin) {
      showToast({ message: "Only admins can delete assets." });
      return;
    }

    // Optimistic remove
    setGear((prev) => (Array.isArray(prev) ? prev.filter((g) => g.id !== id) : []));
    setMenuOpenFor(null);

    try {
      await api.del(`/gear/${id}`);
    } catch (e) {
      console.warn("Failed to delete gear on backend", e);
    }
  };

  // --- Helpers: normalized list + current user (used below) ---
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null')?.id || null; }
    catch { return null; }
  })();

// ---- Transfer handler (store-first, then backend + notifications) ----
const handleGearTransfer = async (itemId, newAssignedTo, newProject) => {
  const notifId = `${itemId}_${Date.now()}`;

  // 1) Update UI/store immediately
  setGear((prev) => {
    if (!Array.isArray(prev)) return [];
    return prev.map((g) => {
      if (g.id !== itemId) return g;
      const fromTo = {
        from: { userId: g.assignedTo || "", projectId: g.assignedProject || "" },
        to:   { userId: newAssignedTo, projectId: newProject || "" },
      };
      return {
        ...g,
        pendingTransfer: {
          notifId,
          ...fromTo,
          date: new Date().toISOString(),
          requestedBy: currentUserId,
        },
      };
    });
  });

  // 2) Persist pendingTransfer on backend
  try {
    await api.post(`/gear/${itemId}/transfer`, {
      notifId,
      to:   { userId: newAssignedTo, projectId: newProject || "" },
      from: (() => {
        const g = (Array.isArray(gear) ? gear : []).find(x => x.id === itemId);
        return { userId: g?.assignedTo || "", projectId: g?.assignedProject || "" };
      })(),
      requestedBy: currentUserId,
    });
  } catch (e) {
    console.warn("Failed to persist pending transfer", e);
  }

  // 3) Create recipient notification on backend
  try {
    await api.post("/notifications", {
      notifId,
      type: "gear-transfer",
      userIds: [newAssignedTo],
      payload: {
        gearId: itemId,
        from: (() => {
          const g = (Array.isArray(gear) ? gear : []).find(x => x.id === itemId);
          return { userId: g?.assignedTo || "", projectId: g?.assignedProject || "" };
        })(),
        to:   { userId: newAssignedTo, projectId: newProject || "" },
        requestedBy: currentUserId,
      },
    });
  } catch (e) {
    console.warn("Failed to create notification", e);
  }

  setShowTransferModal(false);
};

  const handleMenuOpen = (id, btnEl) => {
    setMenuOpenFor((prev) => {
      // Toggle close
      if (prev === id) {
        activeMenuBtnRef.current = null;
        return null;
      }

      // Store the exact clicked button + compute BEFORE showing (no flicker)
      activeMenuBtnRef.current = btnEl;
      computeMenuPosition(btnEl, menuRef.current);
      return id;
    });
  };

  function handleAddField() {
    setFields([...fields, { name: "", label: "", type: "text", required: false }]);
  }
  function handleEditField(idx, key, value) {
    const updated = [...fields];
    updated[idx][key] = value;
    setFields(updated);
  }
  function handleDeleteField(idx) {
    const updated = [...fields];
    updated.splice(idx, 1);
    setFields(updated);
  }
  function handleResetFields() {
    setFields(defaultFields);
  }
  function handleDnD(result) {
    if (!result.destination) return;
    const reordered = Array.from(fields);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setFields(reordered);
  }

  const [catInput, setCatInput] = useState("");
  const [editCatIdx, setEditCatIdx] = useState(null);
  const [editCatValue, setEditCatValue] = useState("");

  // Responsive check
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Display helpers currently handled inline (safeTeam/safeProjects .find)

  // Pending transfer accept/deny UI is not rendered in GearTable (handled elsewhere)

  // ----------- HEADER AND BUTTONS -----------

  const renderHeader = () => (
    <div className="flex flex-col gap-3 mb-4">
      {/* Desktop: Title + Settings + Transfer + Add (single row) */}
      <div className="hidden md:flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-800">Gear</h2>
        </div>

        <div className="flex items-center gap-2">
          {/* Settings button (icon + text) LEFT of Transfer */}
          <div className="relative" ref={gearMenuRef}>
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 py-2 text-sm font-semibold flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setGearMenuOpen((v) => !v);
              }}
              aria-label="Settings"
            >
              <Settings size={18} />
              Settings
            </button>

            {gearMenuOpen && (
              <div className="absolute left-0 mt-2 z-50 w-64 max-w-[calc(100vw-1.5rem)] bg-white border rounded-lg shadow-xl py-2">
                <button
                  className="flex items-center w-full px-4 py-2 hover:bg-gray-100 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGearMenuOpen(false);
                    exportGearAsCSV(fields, filtered, { team: safeTeam, projects: safeProjects });
                  }}
                >
                  <Download size={17} className="mr-2" /> Export Gear – CSV
                </button>

                <button
                  className="flex items-center w-full px-4 py-2 hover:bg-gray-100 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGearMenuOpen(false);
                    exportGearAsPDF(fields, filtered, { team: safeTeam, projects: safeProjects });
                  }}
                >
                  <FileText size={17} className="mr-2" /> Export Gear – PDF
                </button>

                {isAdminOrManager && (
                  <>
                    <hr className="my-2" />

                    {isAdmin && (
                      <button
                        className="flex items-center w-full px-4 py-2 hover:bg-red-50 text-sm text-red-700"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setGearMenuOpen(false);
                          setPurgePassword("");
                          setPurgeError("");
                          setShowPurgeModal(true);
                        }}
                      >
                        <Trash2 size={17} className="mr-2" /> Delete ALL Gear (Admin)
                      </button>
                    )}

                    <label className="flex items-center w-full px-4 py-2 hover:bg-gray-100 text-sm cursor-pointer">
                      <Upload size={17} className="mr-2" /> Upload Gear in Bulk – CSV
                      <input
                        type="file"
                        accept=".csv"
                        style={{ display: "none" }}
                        onChange={importCSVtoGear({
                          fields,
                          setShowImportError,
                          onImport: async (rows) => {
                            try {
                              setShowImportError("");
                              const created = await Promise.all(
                                rows.map(async (r) => {
                                  const res = await api.post("/gear", r);
                                  return res;
                                })
                              );

                              setGear((prev) => {
                                const base = Array.isArray(prev) ? prev : [];
                                const safeCreated = created.filter(Boolean);
                                return [...safeCreated, ...base];
                              });

                              setGearMenuOpen(false);
                              setShowImportSuccess("Successfully uploaded " + rows.length + " items!");
                              setTimeout(() => setShowImportSuccess(""), 3500);
                            } catch (e) {
                              const msg =
                                e?.response?.data?.error ||
                                e?.response?.data?.message ||
                                e?.message ||
                                "Server error";
                              setShowImportError(msg);
                            }
                          },
                        })}
                      />
                    </label>

                    {showImportError && (
                      <div className="text-xs text-red-600 px-4 py-2">{showImportError}</div>
                    )}
                    {showImportSuccess && (
                      <div className="text-xs text-green-600 px-4 py-2">{showImportSuccess}</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Correct Transfer button (keep this one) */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowTransferModal(true);
            }}
            className="bg-green-600 text-white hover:bg-green-700 rounded-lg px-4 py-2 text-sm font-semibold transition flex items-center justify-center"
          >
            <Repeat size={16} className="mr-1" /> Transfer Equipment
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCurrentPage && setCurrentPage("gearAdd");
              navigate("/gear/add");
            }}
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2 text-sm font-semibold transition"
          >
            + Add Equipment
          </button>
        </div>
      </div>

      {/* Desktop: Stats row between title row and search row */}
      <div className="hidden md:block">
        {statsRow || null}
      </div>

      {/* Mobile: keep existing layout (no mobile header changes requested) */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-2xl font-bold text-gray-800">Gear</h2>

          <div className="relative" ref={gearMenuRef}>
            <button
              className="p-2 rounded-full hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                setGearMenuOpen((v) => !v);
              }}
              aria-label="Gear Options"
            >
              <Settings size={22} />
            </button>

            {gearMenuOpen && (
              <div className="absolute right-0 mt-2 z-50 w-64 max-w-[calc(100vw-1.5rem)] bg-white border rounded-lg shadow-xl py-2">
                <button
                  className="flex items-center w-full px-4 py-2 hover:bg-gray-100 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGearMenuOpen(false);
                    exportGearAsCSV(fields, filtered, { team: safeTeam, projects: safeProjects });
                  }}
                >
                  <Download size={17} className="mr-2" /> Export Gear – CSV
                </button>

                <button
                  className="flex items-center w-full px-4 py-2 hover:bg-gray-100 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGearMenuOpen(false);
                    exportGearAsPDF(fields, filtered, { team: safeTeam, projects: safeProjects });
                  }}
                >
                  <FileText size={17} className="mr-2" /> Export Gear – PDF
                </button>

                {isAdminOrManager && (
                  <>
                    <hr className="my-2" />

                    {isAdmin && (
                      <button
                        className="flex items-center w-full px-4 py-2 hover:bg-red-50 text-sm text-red-700"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setGearMenuOpen(false);
                          setPurgePassword("");
                          setPurgeError("");
                          setShowPurgeModal(true);
                        }}
                      >
                        <Trash2 size={17} className="mr-2" /> Delete ALL Gear (Admin)
                      </button>
                    )}

                    <label className="flex items-center w-full px-4 py-2 hover:bg-gray-100 text-sm cursor-pointer">
                      <Upload size={17} className="mr-2" /> Upload Gear in Bulk – CSV
                      <input
                        type="file"
                        accept=".csv"
                        style={{ display: "none" }}
                        onChange={importCSVtoGear({
                          fields,
                          setShowImportError,
                          onImport: async (rows) => {
                            try {
                              setShowImportError("");
                              const created = await Promise.all(
                                rows.map(async (r) => {
                                  const res = await api.post("/gear", r);
                                  return res;
                                })
                              );

                              setGear((prev) => {
                                const base = Array.isArray(prev) ? prev : [];
                                const safeCreated = created.filter(Boolean);
                                return [...safeCreated, ...base];
                              });

                              setGearMenuOpen(false);
                              setShowImportSuccess("Successfully uploaded " + rows.length + " items!");
                              setTimeout(() => setShowImportSuccess(""), 3500);
                            } catch (e) {
                              const msg =
                                e?.response?.data?.error ||
                                e?.response?.data?.message ||
                                e?.message ||
                                "Server error";
                              setShowImportError(msg);
                            }
                          },
                        })}
                      />
                    </label>

                    {showImportError && (
                      <div className="text-xs text-red-600 px-4 py-2">{showImportError}</div>
                    )}
                    {showImportSuccess && (
                      <div className="text-xs text-green-600 px-4 py-2">{showImportSuccess}</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowTransferModal(true);
            }}
            className="bg-green-600 text-white hover:bg-green-700 rounded-lg px-4 py-2 text-sm font-semibold transition flex items-center justify-center w-full"
          >
            <Repeat size={16} className="mr-1" /> Transfer Equipment
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentPage && setCurrentPage("gearAdd");
              navigate("/gear/add");
            }}
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2 text-sm font-semibold transition w-full"
          >
            + Add Equipment
          </button>
        </div>

        {/* Mobile: Manage buttons BELOW Transfer/Add */}
        {isAdminOrManager && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCatModal(true);
              }}
              className="border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap w-full"
            >
              Manage Categories
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowFieldsModal(true);
              }}
              className="border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap w-full"
            >
              Manage Fields
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ----------- TABLES & CARDS -----------
  const renderTable = () => (
    <div className="overflow-visible">
      <table className="min-w-full overflow-visible">
        <thead>
          <tr className="bg-blue-50 text-blue-800">
            <th className="py-1.5 px-2"></th>
            {fields.map((f) => (
              <th key={f.name} className="py-1.5 px-2 text-left font-bold text-sm">
                {f.label}
              </th>
            ))}
            <th className="py-1.5 px-2"></th>
          </tr>
        </thead>
        <tbody className="overflow-visible">
          {filtered.map((g) => (
            <tr
              key={g.id}
              className="border-t hover:bg-blue-50 overflow-visible cursor-pointer"
              onClick={(e) => {
                if (
                  e.target.closest(
                    "button,svg,path,label,input,select"
                  )
                )
                  return;
                setSelectedGear(g);
                setCurrentPage('gearDetail');
              }}
            >
              <td className="py-1.5 px-2">
                <img
                  src={g.imageUrl || "/gear-placeholder.png"}
                  alt={g.name}
                  className="w-7 h-7 object-cover rounded-full bg-gray-100 border"
                />
              </td>
              {fields.map((f) => (
                <td key={f.name} className="py-1.5 px-2 text-sm">
                  {f.name === "category" && g[f.name]}
                  {f.name === "status" && g[f.name]}
                  {f.name === "assignedTo" &&
                    (g.assignedTo
                      ? safeTeam.find((u) => u.id === g.assignedTo)?.name || (
                          <span className="text-gray-400">Not found</span>
                        )
                      : <span className="text-gray-400">Unassigned</span>)}
                  {f.name === "assignedProject" &&
                    (g.assignedProject
                      ? safeProjects.find((p) => p.id === g.assignedProject)?.name || (
                          <span className="text-gray-400">Not found</span>
                        )
                      : <span className="text-gray-400">Unassigned</span>)}
                  {["name", "serialNumber", "notes"].includes(f.name) &&
                    g[f.name]}
                  {f.name === "dateAdded" &&
                    (g.dateAdded
                      ? new Date(g.dateAdded).toLocaleDateString()
                      : "")}
                  {["text", "select", "textarea", "date", "number", "checkbox"].includes(
                    f.type
                  ) &&
                    ![
                      "category",
                      "status",
                      "assignedTo",
                      "assignedProject",
                      "name",
                      "serialNumber",
                      "notes",
                      "dateAdded",
                    ].includes(f.name) &&
                    String(g[f.name] ?? "")}
                </td>
              ))}
              <td className="py-1.5 px-2 flex gap-2 relative overflow-visible">
                <span>
                  <button
                    ref={(el) => (btnRefs.current[g.id] = el)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(g.id, e.currentTarget);
                    }}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <MoreVertical size={16} />
                  </button>
                  {menuOpenFor === g.id && (
                    <MenuDropdown
                      ref={menuRef}
                      position={menuPosition}
                      onEdit={(e) => {
                        e.stopPropagation();
                        setModalGear(g);
                        setShowModal(true);
                        setMenuOpenFor(null);
                      }}
                      onDelete={(e) => {
                        e.stopPropagation();
                        handleDeleteGear(g.id);
                      }}
                      onArchiveToggle={async (e) => {
                        e.stopPropagation();
                        setMenuOpenFor(null);
                        await handleArchiveToggle(g);
                      }}
                      isArchived={!!g.isArchived}
                      canAdminActions={isAdmin}
                    />
                  )}
                </span>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={fields.length + 2} className="py-4 text-center text-gray-400">
                No gear found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ----------- MOBILE CARD VIEW -----------
  const renderMobileCards = () => (
    <div className="flex flex-col gap-2">
      {filtered.map((g) => {
        const assignedToName = g.assignedTo
          ? safeTeam.find((u) => u.id === g.assignedTo)?.name || null
          : null;

        const projectName = g.assignedProject
          ? safeProjects.find((p) => p.id === g.assignedProject)?.name || null
          : null;

        return (
          <div
            key={g.id}
            tabIndex={-1}
            className="flex items-start bg-white rounded-xl shadow-sm p-2.5 relative cursor-pointer"
            onClick={(e) => {
              if (e.target.closest("button,svg,path,label,input,select")) return;
              setSelectedGear(g);
              setCurrentPage("gearDetail");
            }}
          >
            <img
              src={g.imageUrl || "/gear-placeholder.png"}
              alt={g.name}
              className="w-10 h-10 object-cover rounded-full bg-gray-100 border mr-2 mt-0.5"
            />

            <div className="flex-1 min-w-0 pr-8">
              <div className="font-semibold text-base leading-5 truncate">{g.name}</div>

              {/* Two-column info row (compact) */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1">
                {/* Left column: Serial + Category */}
                <div className="min-w-0">
                  <div className="text-slate-400 text-xs truncate">
                    {g.serialNumber || <span>&mdash;</span>}
                  </div>
                  <div className="text-slate-600 text-xs font-medium truncate">
                    {g.category || <span className="text-gray-400">&mdash;</span>}
                  </div>
                </div>

                {/* Right column: Assigned + Project */}
                <div className="min-w-0">
                  <div className="text-slate-500 text-xs truncate">
                    <span className="text-slate-400">Assigned:</span>{" "}
                    {assignedToName ? assignedToName : <span className="text-gray-400">—</span>}
                  </div>
                  <div className="text-slate-500 text-xs truncate">
                    <span className="text-slate-400">Project:</span>{" "}
                    {projectName ? projectName : <span className="text-gray-400">—</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Three-dot menu */}
            <button
              ref={(el) => (btnRefs.current[g.id] = el)}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(g.id, e.currentTarget);
              }}
              className="p-1 hover:bg-gray-100 rounded absolute right-2 top-2"
            >
              <MoreVertical size={18} />
            </button>

            {menuOpenFor === g.id && (
              <MenuDropdown
                ref={menuRef}
                position={menuPosition}
                onEdit={(e) => {
                  e.stopPropagation();
                  setModalGear(g);
                  setShowModal(true);
                  setMenuOpenFor(null);
                }}
                onDelete={(e) => {
                  e.stopPropagation();
                  handleDeleteGear(g.id);
                }}
                onArchiveToggle={async (e) => {
                  e.stopPropagation();
                  setMenuOpenFor(null);
                  await handleArchiveToggle(g);
                }}
                isArchived={!!g.isArchived}
                canAdminActions={isAdmin}
              />
            )}
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="text-center text-gray-400 py-8">No gear found</div>
      )}
    </div>
  );

  // ----------- FINAL RETURN -----------
  return (
    <>
      {renderHeader()}

      {/* Mobile: Stats ABOVE search/filter */}
      <div className="md:hidden mb-2">
        {statsRow || null}
      </div>

      {/* Search + Filter row */}
      <div className="w-full mb-3 md:mb-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 items-center">
          {/* LEFT (desktop ~50%): Search + Filter */}
          <div className="flex items-center gap-2 w-full">
            <input
              type="text"
              name="gearSearch"
              autoComplete="off"
              placeholder="Search gear..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border rounded px-3 py-2 md:py-1.5 flex-1 min-w-0 text-sm"
            />

            {/* Filter button + dropdown */}
            <div ref={filterMenuRef} className="relative">
              <button
                ref={filterBtnRef}
                className="p-2 rounded-lg hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterOpen((v) => !v);
                }}
                aria-label="Filter"
              >
                <Filter size={22} />
              </button>

              {filterOpen && (
                <div className="absolute right-0 mt-2 z-50 w-64 max-w-[calc(100vw-1.5rem)] bg-white border rounded-lg shadow-xl p-3">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Filters</div>

                  <label className="block text-xs text-gray-500 mb-1">Category</label>
                  <select
                    value={filter.category}
                    onChange={(e) => setFilter((prev) => ({ ...prev, category: e.target.value }))}
                    className="border rounded px-2 py-2 w-full mb-3"
                  >
                    <option value="All">All</option>
                    {safeCategories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <select
                    value={filter.status}
                    onChange={(e) => setFilter((prev) => ({ ...prev, status: e.target.value }))}
                    className="border rounded px-2 py-2 w-full"
                  >
                    <option value="All">All</option>
                    {safeStatuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT (desktop rest): Manage buttons */}
          <div className="hidden md:flex items-center justify-end gap-2">
            {isAdminOrManager && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCatModal(true);
                  }}
                  className="border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap w-[170px] text-center"
                >
                  Manage Categories
                </button>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFieldsModal(true);
                  }}
                  className="border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap w-[170px] text-center"
                >
                  Manage Fields
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isMobile ? renderMobileCards() : renderTable()}

      {/* --- Transfer Equipment Modal --- */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div ref={transferModalRef} className="relative z-50">
            <TransferEquipmentModal
              gearList={safeGear}
              team={safeTeam}
              projects={safeProjects}
              onClose={() => setShowTransferModal(false)}
              onTransfer={handleGearTransfer}
            />
          </div>
        </div>
      )}

      {/* --- Add/Edit, Category, Fields Modals --- */}
      {showModal && (
        <GearModal
          modalRef={modalRef}
          gear={modalGear}
          onClose={() => setShowModal(false)}
          onSave={handleSaveGear}
          team={safeTeam.filter((u) => u.active)}
          projects={safeProjects.filter((p) => p.active)}
          categories={categories}
          statuses={statuses}
          fields={fields}
          isAdminOrManager={isAdminOrManager}
        />
      )}

      {/* --- Category Modal --- */}
      {showCatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div ref={catModalRef} className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-xl">Manage Categories</h3>
              <button
                onClick={() => setShowCatModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            <ul className="mb-4">
              {categories.map((cat, i) => (
                <li key={cat} className="flex items-center gap-2 mb-2">
                  {editCatIdx === i ? (
                    <>
                      <input
                        value={editCatValue}
                        onChange={(e) => setEditCatValue(e.target.value)}
                        className="border rounded px-2 py-1 flex-1"
                      />
                      <button
                        className="text-blue-600 text-sm px-2"
                        onClick={() => {
                          const updated = [...categories];
                          updated[i] = editCatValue;
                          setCategories(updated);
                          setEditCatIdx(null);
                        }}
                      >
                        Save
                      </button>
                      <button
                        className="text-gray-500 px-2"
                        onClick={() => setEditCatIdx(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1">{cat}</span>
                      <button
                        className="text-blue-600 px-2 text-sm"
                        onClick={() => {
                          setEditCatIdx(i);
                          setEditCatValue(cat);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-600 px-2 text-sm"
                        onClick={() => {
                          const updated = categories.filter((_, idx) => idx !== i);
                          setCategories(updated);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!catInput.trim()) return;
                setCategories([...categories, catInput.trim()]);
                setCatInput("");
              }}
              className="flex gap-2"
            >
              <input
                value={catInput}
                onChange={(e) => setCatInput(e.target.value)}
                placeholder="Add category"
                className="border rounded px-2 py-1 flex-1"
              />
              <button
                type="submit"
                className="bg-blue-600 text-white rounded px-3 py-1 text-sm"
              >
                Add
              </button>
            </form>
          </div>
        </div>
      )}

      {/* --- Fields Modal --- */}
      {showPurgeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center px-4">
          <div
            ref={purgeModalRef}
            className="bg-white rounded-xl shadow-lg w-full max-w-md p-6"
            role="dialog"
            aria-modal="true"
            aria-label="Delete all gear confirmation"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-xl text-red-700">Delete ALL Gear</h3>
              <button
                type="button"
                onClick={() => {
                  setShowPurgeModal(false);
                  setPurgeBusy(false);
                  setPurgeError("");
                  setPurgePassword("");
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="text-sm text-gray-700 mb-4">
              <div className="font-semibold text-gray-900 mb-1">ADMIN ONLY</div>
              This will permanently delete <span className="font-semibold">all</span> gear items.
              This cannot be undone.
            </div>

            <label className="block text-xs text-gray-500 mb-1">Enter ADMIN purge password</label>
            <input
              type="password"
              name="adminPurgePassword"
              autoComplete="new-password"
              value={purgePassword}
              onChange={(e) => setPurgePassword(e.target.value)}
              className="border rounded px-3 py-2 w-full mb-2"
              placeholder="Password"
              autoFocus
            />

            {purgeError && (
              <div className="text-xs text-red-600 mb-3">{purgeError}</div>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowPurgeModal(false);
                  setPurgeBusy(false);
                  setPurgeError("");
                  setPurgePassword("");
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
                disabled={purgeBusy}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={async () => {
                  if (purgeBusy) return;

                  setPurgeError("");
                  if (!purgePassword) {
                    setPurgeError("Password is required.");
                    return;
                  }

                  try {
                    setPurgeBusy(true);

                    await api.post("/gear/purge", { password: purgePassword });

                    setGear([]);
                    setShowPurgeModal(false);
                    setPurgePassword("");
                    setPurgeError("");
                    setShowImportError("");
                    setShowImportSuccess("Deleted all gear items.");
                    setTimeout(() => setShowImportSuccess(""), 3500);
                  } catch (err) {
                    const msg =
                      err?.response?.data?.error ||
                      err?.response?.data?.message ||
                      err?.message ||
                      "Failed to delete all gear.";
                    setPurgeError(msg);
                  } finally {
                    setPurgeBusy(false);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-60"
                disabled={purgeBusy}
              >
                {purgeBusy ? "Deleting..." : "Delete All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFieldsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div ref={fieldsModalRef} className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-xl">Manage Fields</h3>
              <button
                onClick={() => setShowFieldsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1" style={{ maxHeight: "50vh" }}>
              <DragDropContext onDragEnd={handleDnD}>
                <Droppable droppableId="fields">
                  {(provided) => (
                    <ul
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="flex flex-col gap-2"
                    >
                      {fields.map((f, i) => (
                        <Draggable key={i} draggableId={String(i)} index={i}>
                          {(provided, snapshot) => (
                            <li
                              className={`flex items-center gap-2 bg-gray-50 p-1 rounded w-full flex-wrap ${
                                snapshot.isDragging ? "shadow-lg" : ""
                              }`}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <span
                                {...provided.dragHandleProps}
                                className="cursor-grab p-1 mr-2"
                                title="Drag to reorder"
                              >
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                  <circle cx="6" cy="6" r="1.5" fill="#888" />
                                  <circle cx="6" cy="10" r="1.5" fill="#888" />
                                  <circle cx="6" cy="14" r="1.5" fill="#888" />
                                  <circle cx="14" cy="6" r="1.5" fill="#888" />
                                  <circle cx="14" cy="10" r="1.5" fill="#888" />
                                  <circle cx="14" cy="14" r="1.5" fill="#888" />
                                </svg>
                              </span>
                              <input
                                value={f.label}
                                onChange={(e) => {
                                  handleEditField(i, "label", e.target.value);
                                  handleEditField(i, "name", toCamelCase(e.target.value));
                                }}
                                placeholder="Label"
                                className="border rounded px-2 py-1 w-36"
                              />
                              <select
                                value={f.type}
                                onChange={(e) =>
                                  handleEditField(i, "type", e.target.value)
                                }
                                className="border rounded px-2 py-1 w-36"
                              >
                                <option value="text">Text</option>
                                <option value="select">Dropdown</option>
                                <option value="textarea">Paragraph</option>
                                <option value="date">Date</option>
                                <option value="number">Number</option>
                                <option value="checkbox">Checkmark</option>
                              </select>
                              <label className="flex items-center text-xs ml-1">
                                <input
                                  type="checkbox"
                                  checked={f.required}
                                  onChange={(e) =>
                                    handleEditField(i, "required", e.target.checked)
                                  }
                                  className="mr-1"
                                />
                                Required
                              </label>
                              <button
                                className="text-red-600 px-2 text-sm"
                                onClick={() => handleDeleteField(i)}
                                tabIndex={-1}
                                title="Delete field"
                              >
                                <Trash2 size={18} />
                              </button>
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                className="bg-blue-600 text-white rounded px-3 py-1 text-sm"
                onClick={handleAddField}
              >
                + Add Field
              </button>
              <button
                className="bg-gray-300 rounded px-3 py-1 text-sm"
                onClick={handleResetFields}
              >
                Reset to Default
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[99999]">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3">
            <div className="text-sm font-semibold">{toast.message}</div>
            {toast.actionLabel && typeof toast.onAction === "function" && (
              <button
                type="button"
                className="text-sm font-bold underline underline-offset-2"
                onClick={() => {
                  toast.onAction();
                  setToast(null);
                }}
              >
                {toast.actionLabel}
              </button>
            )}
            <button
              type="button"
              className="ml-1 text-white/80 hover:text-white"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// --- MenuDropdown: all buttons use e.stopPropagation() ---
const MenuDropdown = React.forwardRef(function MenuDropdown(
  { position, onEdit, onDelete, onArchiveToggle, isArchived, canAdminActions },
  ref
) {
  const style = {
    position: "fixed",
    left: position.left,
    top: position.top,
    minWidth: 170,
    zIndex: 9999,
    background: "white",
    borderRadius: "0.75rem",
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
  };
  return (
    <div ref={ref} style={style} className="shadow z-[9999]">
      <button
        onClick={onEdit}
        className="flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100"
      >
        <Pencil size={16} className="mr-2 text-blue-600" />
        Edit
      </button>
      <button
        onClick={onArchiveToggle}
        className="flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100"
      >
        <span className="mr-2">{isArchived ? "↩️" : "🗄️"}</span>
        {isArchived ? "Unarchive" : "Archive"}
      </button>

      <button
        onClick={onDelete}
        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
      >
        <Trash2 size={16} className="mr-2 text-red-600" />
        Delete
      </button>
    </div>
  );
});

export default GearTable;
